import { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: 'client' | 'restaurateur' | 'commercant' | 'livreur' | 'admin';
}

export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { user, userRole, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading) {
      if (!user) {
        navigate('/connexion', { replace: true });
        return;
      }

      if (requiredRole && userRole !== requiredRole) {
        // Les admins ont accès à tout
        if (userRole === 'admin') return;

        switch (userRole) {
          case 'client':
            navigate('/dashboard/utilisateur', { replace: true });
            break;
          case 'restaurateur':
            navigate('/dashboard/restaurateur', { replace: true });
            break;
          case 'commercant':
            navigate('/dashboard/commercant', { replace: true });
            break;
          case 'livreur':
            navigate('/dashboard/livreur', { replace: true });
            break;
          case 'admin':
            navigate('/dashboard/admin', { replace: true });
            break;
          default:
            navigate('/connexion', { replace: true });
        }
      }
    }
  }, [user, userRole, loading, requiredRole, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-gray-600">Vérification de votre session...</p>
        </div>
      </div>
    );
  }

  // Les admins ont accès à toutes les pages protégées
  if (user && (userRole === 'admin' || !requiredRole || userRole === requiredRole)) {
    return <>{children}</>;
  }

  return null;
}
